/**
 * UniversalAllergensBlock (Iter 9.40 - Sync Architecture)
 *
 * Cross-category "Safety & preferences" block.
 * Syncs with /api/me/allergens - every allergen/accessibility/dietary flag
 * cascades to every RSVP on any host that has that field enabled.
 *
 * Usage:
 *   <UniversalAllergensBlock />
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, AlertTriangle, Accessibility, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { key: "allergies", label: "Allergies & sensitivities", icon: AlertTriangle, tone: "rose" },
  { key: "accessibility", label: "Accessibility needs", icon: Accessibility, tone: "blue" },
  { key: "dietary", label: "Dietary preferences", icon: UtensilsCrossed, tone: "emerald" },
];

const toneClasses = {
  rose:    { active: "bg-rose-600 text-white border-rose-600",       idle: "bg-white text-gray-700 border-gray-200 hover:border-rose-300" },
  blue:    { active: "bg-blue-600 text-white border-blue-600",       idle: "bg-white text-gray-700 border-gray-200 hover:border-blue-300" },
  emerald: { active: "bg-emerald-600 text-white border-emerald-600", idle: "bg-white text-gray-700 border-gray-200 hover:border-emerald-300" },
};

export default function UniversalAllergensBlock() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canonical, setCanonical] = useState({ allergies: [], accessibility: [], dietary: [] });
  const [selected, setSelected] = useState({ allergies: [], accessibility: [], dietary: [] });

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) { setLoading(false); return; }
    fetch(`${API}/me/allergens`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!d) return;
        setCanonical({
          allergies: d.canonical_allergens || [],
          accessibility: d.canonical_accessibility || [],
          dietary: d.canonical_dietary || [],
        });
        setSelected(d.selected || { allergies: [], accessibility: [], dietary: [] });
      })
      .finally(() => setLoading(false));
  }, []);

  const toggle = (group, opt) => {
    setSelected((prev) => {
      const list = prev[group] || [];
      const next = list.includes(opt) ? list.filter((x) => x !== opt) : [...list, opt];
      return { ...prev, [group]: next };
    });
  };

  const save = async () => {
    const token = localStorage.getItem("session_token");
    if (!token) { toast.error("Login required"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/me/allergens`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(selected),
      });
      if (r.ok) {
        toast.success("Synced to every host you book with.");
      } else {
        toast.error("Could not save");
      }
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </CardContent></Card>
    );
  }

  const totalSelected = selected.allergies.length + selected.accessibility.length + selected.dietary.length;

  return (
    <Card data-testid="universal-allergens-block">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Safety & cross-category preferences</h3>
            <p className="text-xs text-gray-500 mt-1">
              These apply to every experience you plan. Syncs automatically to any host
              whose category includes allergies, accessibility, or dietary preferences.
            </p>
          </div>
          <Badge variant="outline" className="text-[10px]">{totalSelected} selected</Badge>
        </div>

        {GROUPS.map(({ key, label, icon: Icon, tone }) => {
          const opts = canonical[key] || [];
          const sel = selected[key] || [];
          if (!opts.length) return null;
          return (
            <div key={key} data-testid={`allergens-group-${key}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-gray-500" />
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</h4>
                {sel.length > 0 && <span className="text-[10px] text-gray-400">({sel.length})</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {opts.map((opt) => {
                  const active = sel.includes(opt);
                  const cls = toneClasses[tone];
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggle(key, opt)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border capitalize transition-all ${active ? cls.active : cls.idle}`}
                      data-testid={`allergen-opt-${key}-${opt}`}
                    >
                      {opt.replace(/_/g, " ")}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        <Button
          onClick={save}
          disabled={saving}
          className="w-full sm:w-auto h-10 bg-black hover:bg-gray-800"
          data-testid="allergens-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Sync to all my hosts
        </Button>
      </CardContent>
    </Card>
  );
}
